import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Cross, Check, ArrowLeft, Loader2, Sparkles, BookOpen, Music, Bell, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ChristianUpgrade = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  
  const [selectedPlan, setSelectedPlan] = useState('christian');
  const [loading, setLoading] = useState(false);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [loginData, setLoginData] = useState({ email: searchParams.get('email') || '', password: '' });
  
  // Check if user came from login with state
  const stateUser = location.state?.user;

  useEffect(() => {
    if (!isAuthenticated && !stateUser) {
      setNeedsLogin(true);
    }
  }, [isAuthenticated, stateUser]);

  const plans = [
    {
      id: 'christian',
      name: 'Christian Education',
      price: '$2.99',
      period: '/month',
      description: 'Full access to Christian curriculum',
      features: [
        'Full 52-week Bible curriculum',
        'All printable worksheets',
        'Memory verse tracking',
        'Worship songs library',
        'Push notifications',
        'Progress reports'
      ],
      popular: true
    },
    {
      id: 'christian_plus',
      name: 'Christian Plus',
      price: '$4.99',
      period: '/month',
      description: 'Christian + Academic access',
      features: [
        'Everything in Christian Education',
        'AI-powered academic lessons',
        'Reading, Math, Writing, Science',
        '5 AI worksheets per week',
        'Family Story Time',
        'Priority support'
      ],
      popular: false
    }
  ];

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email: loginData.email,
        password: loginData.password
      });
      
      if (response.data.access_token) {
        localStorage.setItem('littlelearners-token', response.data.access_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setNeedsLogin(false);
        toast.success('Logged in successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('littlelearners-token') || localStorage.getItem('token');

      // Real Stripe subscription checkout
      const response = await axios.post(
        `${API_URL}/api/billing/subscribe`,
        {
          plan_key: selectedPlan,
          quantity: 1,
          origin: window.location.origin,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.url) {
        // Redirect to Stripe-hosted checkout
        window.location.href = response.data.url;
        return;
      }

      toast.error('Failed to start checkout');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upgrade failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-950 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <Link to="/christian" className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Christian Education
        </Link>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-4">
            <Cross className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Add Christian Education</h1>
          <p className="text-slate-400">
            {needsLogin 
              ? 'Sign in to your existing account to add Christian Education'
              : 'Upgrade your account to access the full Christian curriculum'}
          </p>
        </div>

        {needsLogin ? (
          /* Login Form */
          <Card className="bg-slate-800/50 border-slate-700 max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="text-white">Sign In First</CardTitle>
              <CardDescription className="text-slate-400">
                Enter your existing Zazu account credentials
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="text-white">Email</Label>
                  <Input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="you@example.com"
                    className="bg-slate-700 border-slate-600 text-white"
                    required
                    data-testid="upgrade-login-email"
                  />
                </div>
                <div>
                  <Label className="text-white">Password</Label>
                  <Input
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter your password"
                    className="bg-slate-700 border-slate-600 text-white"
                    required
                    data-testid="upgrade-login-password"
                  />
                </div>
                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-indigo-500 to-purple-600"
                  data-testid="upgrade-login-submit"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Sign In & Continue
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          /* Plan Selection */
          <>
            {/* Current Account Info */}
            <Card className="bg-slate-800/30 border-slate-700/50 mb-6">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Upgrading account:</p>
                  <p className="text-white font-medium">{user?.email || stateUser?.email || 'Your Account'}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-400 text-sm">Current plan:</p>
                  <p className="text-purple-400 font-medium capitalize">{user?.subscription_plan || stateUser?.subscription_plan || 'Free'}</p>
                </div>
              </CardContent>
            </Card>

            {/* Plans */}
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {plans.map((plan) => (
                <Card 
                  key={plan.id}
                  className={`cursor-pointer transition-all ${
                    selectedPlan === plan.id 
                      ? 'bg-gradient-to-br from-indigo-900/50 to-purple-900/50 border-purple-500 ring-2 ring-purple-500/50' 
                      : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                  }`}
                  onClick={() => setSelectedPlan(plan.id)}
                  data-testid={`plan-${plan.id}`}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-white flex items-center gap-2">
                        {plan.id === 'christian_plus' && <Crown className="w-5 h-5 text-amber-400" />}
                        {plan.name}
                      </CardTitle>
                      {plan.popular && (
                        <span className="bg-purple-500 text-white text-xs px-2 py-1 rounded-full">
                          Popular
                        </span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-white">{plan.price}</span>
                      <span className="text-slate-400">{plan.period}</span>
                    </div>
                    <CardDescription className="text-slate-400">{plan.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-slate-300 text-sm">
                          <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* What You Get Section */}
            <Card className="bg-slate-800/30 border-slate-700/50 mb-8">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  What's Included
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <BookOpen className="w-8 h-8 text-indigo-400" />
                    <div>
                      <p className="text-white font-medium text-sm">52 Weeks</p>
                      <p className="text-slate-400 text-xs">Bible Curriculum</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Music className="w-8 h-8 text-pink-400" />
                    <div>
                      <p className="text-white font-medium text-sm">Worship</p>
                      <p className="text-slate-400 text-xs">Songs Library</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Bell className="w-8 h-8 text-amber-400" />
                    <div>
                      <p className="text-white font-medium text-sm">Reminders</p>
                      <p className="text-slate-400 text-xs">Push Notifications</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg">
                    <Cross className="w-8 h-8 text-purple-400" />
                    <div>
                      <p className="text-white font-medium text-sm">Picture Login</p>
                      <p className="text-slate-400 text-xs">Kid-Friendly</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Upgrade Button */}
            <div className="text-center">
              <Button
                onClick={handleUpgrade}
                disabled={loading}
                size="lg"
                className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 px-12"
                data-testid="upgrade-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Upgrade to {plans.find(p => p.id === selectedPlan)?.name}
                  </>
                )}
              </Button>
              <p className="text-slate-500 text-sm mt-3">
                Cancel anytime • 7-day money-back guarantee
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChristianUpgrade;
